'use client';

import { useEffect, useState, useMemo } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { cn } from '@/platform/ui/utils';
import { Button } from '@/platform/ui/button';
import { ScrollArea } from '@/platform/ui/scroll-area';
import { Separator } from '@/platform/ui/separator';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/platform/ui/collapsible';
import {
  LayoutDashboard,
  Package,
  Briefcase,
  Settings,
  Users,
  Shield,
  Palette,
  X,
  ClipboardList,
  Lightbulb,
  FileText,
  ChevronDown,
  ChevronRight,
  Building2,
  UserPlus,
  UserCheck,
  Mail,
  Database,
  LayoutGrid,
  Plug,
  Layers,
  MessageSquare,
} from 'lucide-react';
import { useUserSessionData } from '@/platform/auth/user-session-context';
import { PLATFORM_PERMISSIONS, ORGANIZATION_PERMISSIONS } from '@/platform/constants/permissions';
import { useProductBranding, getProductIcon, SIDEBAR_DEFAULT_ICONS, type ProductBranding } from '@/platform/hooks/use-product-branding';

interface NavItem {
  label: string;
  href?: string;
  icon: React.ReactNode;
  requiresProduct?: 'productA' | 'productB';
  requiresAdmin?: boolean;
  requiresPlatformPermission?: string;
  requiresOrgPermission?: string;
  children?: NavItem[];
}

function buildNavItems(branding: ProductBranding): NavItem[] {
  return [
    {
      label: 'Dashboard',
      href: '/dashboard',
      icon: <LayoutDashboard className="h-5 w-5" />,
    },
    {
      label: branding.products.productA.name,
      href: '/products/productA',
      icon: getProductIcon('productA', branding, 'h-5 w-5'),
      requiresProduct: 'productA',
    },
    {
      label: branding.products.productB.name,
      href: '/products/productB',
      icon: getProductIcon('productB', branding, 'h-5 w-5'),
      requiresProduct: 'productB',
    },
  ];
}

function buildAdminItems(branding: ProductBranding): NavItem[] {
  return [
    {
      label: 'Users',
      href: '/admin/users',
      icon: <Users className="h-5 w-5" />,
      requiresAdmin: true,
    },
    {
      label: 'Organisations',
      href: '/admin/companies',
      icon: <Building2 className="h-5 w-5" />,
      requiresAdmin: true,
    },
    {
      label: 'Onboarding',
      icon: <UserPlus className="h-5 w-5" />,
      requiresAdmin: true,
      children: [
        {
          label: 'Join Requests',
          href: '/admin/onboarding/join-requests',
          icon: <UserCheck className="h-4 w-4" />,
        },
        {
          label: 'Invitations',
          href: '/admin/onboarding/invitations',
          icon: <Mail className="h-4 w-4" />,
        },
      ],
    },
    {
      label: branding.products.productA.name,
      icon: getProductIcon('productA', branding, 'h-5 w-5'),
      requiresPlatformPermission: PLATFORM_PERMISSIONS.MANAGE_PRODUCT_A,
      children: [
        { label: 'Overview', href: '/admin/productA', icon: <LayoutGrid className="h-4 w-4" /> },
        { label: 'Questions', href: '/admin/productA/questions', icon: <ClipboardList className="h-4 w-4" /> },
        { label: 'Recommendations', href: '/admin/productA/recommendations', icon: <Lightbulb className="h-4 w-4" /> },
        { label: 'Assessments', href: '/admin/productA/assessments', icon: <FileText className="h-4 w-4" /> },
      ],
    },
    {
      label: branding.products.productB.name,
      icon: getProductIcon('productB', branding, 'h-5 w-5'),
      requiresPlatformPermission: PLATFORM_PERMISSIONS.MANAGE_PRODUCT_B,
      children: [
        { label: 'Overview', href: '/admin/productB', icon: <LayoutGrid className="h-4 w-4" /> },
        { label: 'Sessions', href: '/admin/productB/sessions', icon: <Users className="h-4 w-4" /> },
        { label: 'Question Banks', href: '/admin/productB/question-banks', icon: <Database className="h-4 w-4" /> },
        { label: 'Archetypes', href: '/admin/productB/archetypes', icon: <Layers className="h-4 w-4" /> },
        { label: 'Assessments', href: '/admin/productB/assessments', icon: <FileText className="h-4 w-4" /> },
      ],
    },
    {
      label: 'Settings',
      icon: <Settings className="h-5 w-5" />,
      requiresAdmin: true,
      children: [
        { label: 'System Settings', href: '/admin/settings', icon: <Settings className="h-4 w-4" /> },
        { label: 'Data Settings', href: '/admin/settings/data', icon: <Database className="h-4 w-4" /> },
        { label: 'Email', href: '/admin/settings/email', icon: <Mail className="h-4 w-4" /> },
        { label: 'Completion Messages', href: '/admin/settings/completion-messages', icon: <MessageSquare className="h-4 w-4" /> },
        { label: 'Integrations', href: '/admin/settings/integrations', icon: <Plug className="h-4 w-4" /> },
        { label: 'Design System', href: '/admin/design-system', icon: <Palette className="h-4 w-4" /> },
      ],
    },
  ];
}

interface AppSidebarProps {
  open: boolean;
  onClose: () => void;
}

export function AppSidebar({ open, onClose }: AppSidebarProps) {
  const pathname = usePathname();
  const { entitlements, isAdmin, isSuperAdmin, platformPermissions, activeOrganization } = useUserSessionData();
  const { branding } = useProductBranding();
  const [expandedItems, setExpandedItems] = useState<string[]>([]);

  const navItems = useMemo(() => buildNavItems(branding), [branding]);
  const adminItems = useMemo(() => buildAdminItems(branding), [branding]);

  useEffect(() => {
    const expanded: string[] = [];
    adminItems.forEach((item) => {
      if (item.children) {
        const hasActiveChild = item.children.some(
          (child) => child.href && pathname.startsWith(child.href)
        );
        if (hasActiveChild) {
          expanded.push(item.label);
        }
      }
    });
    setExpandedItems(expanded);
  }, [pathname, adminItems]);

  const toggleExpanded = (label: string) => {
    setExpandedItems((prev) =>
      prev.includes(label) ? prev.filter((item) => item !== label) : [...prev, label]
    );
  };

  const filteredNavItems = useMemo(() => navItems.filter(item => {
    if (item.requiresProduct) {
      const hasProduct = isSuperAdmin || entitlements.includes(item.requiresProduct);
      if (!hasProduct) return false;
    }
    if (item.requiresOrgPermission) {
      const perm = item.requiresOrgPermission;
      const hasOrgPerm = isSuperAdmin || isAdmin ||
        (activeOrganization?.isOwner) ||
        (activeOrganization?.permissions?.includes(perm as any));
      if (!hasOrgPerm) return false;
    }
    return true;
  }), [navItems, entitlements, isSuperAdmin, isAdmin, activeOrganization]);

  const filteredAdminItems = useMemo(() => adminItems.filter(item => {
    if (item.requiresAdmin) {
      return isAdmin;
    }
    if (item.requiresPlatformPermission) {
      return isAdmin || platformPermissions.includes(item.requiresPlatformPermission);
    }
    return true;
  }), [adminItems, isAdmin, platformPermissions]);

  const isParentActive = (item: NavItem) => {
    if (!item.children) return false;
    return item.children.some((child) => child.href && pathname.startsWith(child.href));
  };

  return (
    <>
      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-50 w-[var(--sidebar-width)] bg-card border-r transform transition-transform duration-slow ease-out lg:relative lg:translate-x-0',
          open ? 'translate-x-0' : '-translate-x-full'
        )}
      >
        <div className="flex items-center justify-between h-[var(--header-height)] px-6 border-b">
          <Link href="/dashboard" className="flex items-center space-x-2">
            {branding.containerIconUrl ? (
              <img src={branding.containerIconUrl} alt={branding.containerName} className="w-8 h-8 rounded-lg object-contain" />
            ) : (
              <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center">
                <span className="text-primary-foreground font-bold text-lg">{branding.containerName.charAt(0)}</span>
              </div>
            )}
            <span className="font-bold text-lg">{branding.containerName}</span>
          </Link>
          <Button
            variant="ghost"
            size="icon"
            className="lg:hidden"
            onClick={onClose}
          >
            <X className="h-5 w-5" />
          </Button>
        </div>

        <ScrollArea className="flex-1 py-4">
          <nav className="space-y-1 px-3">
            {filteredNavItems.map((item) => {
              if (!item.href) return null;
              return (
                <Link
                  key={item.href}
                  href={item.href}
                  className={cn(
                    'flex items-center space-x-3 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-fast',
                    pathname === item.href
                      ? 'bg-primary text-primary-foreground shadow-xs'
                      : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                  )}
                >
                  {item.icon}
                  <span>{item.label}</span>
                </Link>
              );
            })}

            {filteredAdminItems.length > 0 && (
              <>
                <Separator className="my-4" />
                <div className="px-3 py-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                  Administration
                </div>
                {filteredAdminItems.map((item) => {
                  if (item.children && item.children.length > 0) {
                    const isExpanded = expandedItems.includes(item.label);
                    return (
                      <Collapsible
                        key={item.label}
                        open={isExpanded}
                        onOpenChange={() => toggleExpanded(item.label)}
                      >
                        <CollapsibleTrigger asChild>
                          <button
                            className={cn(
                              'w-full flex items-start justify-between px-3 py-2 rounded-lg text-sm font-medium transition-all duration-fast',
                              isParentActive(item)
                                ? 'bg-accent text-accent-foreground'
                                : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                            )}
                          >
                            <div className="flex items-start space-x-3">
                              <span className="mt-0.5 shrink-0">{item.icon}</span>
                              <span className="text-left">{item.label}</span>
                            </div>
                            {isExpanded ? (
                              <ChevronDown className="h-4 w-4 mt-0.5 shrink-0" />
                            ) : (
                              <ChevronRight className="h-4 w-4 mt-0.5 shrink-0" />
                            )}
                          </button>
                        </CollapsibleTrigger>
                        <CollapsibleContent className="pl-6 space-y-1 mt-1">
                          {item.children.map((child) => {
                            if (!child.href) return null;
                            const isOverviewItem = child.label === 'Overview' && item.children && item.children.length > 1;
                            const isChildActive = isOverviewItem
                              ? pathname === child.href
                              : pathname === child.href || (child.href !== '/admin/settings' && pathname.startsWith(child.href + '/'));
                            return (
                              <Link
                                key={child.href}
                                href={child.href}
                                className={cn(
                                  'flex items-center space-x-3 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-fast',
                                  isChildActive
                                    ? 'bg-primary text-primary-foreground shadow-xs'
                                    : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                                )}
                              >
                                {child.icon}
                                <span>{child.label}</span>
                              </Link>
                            );
                          })}
                        </CollapsibleContent>
                      </Collapsible>
                    );
                  }
                  if (!item.href) return null;
                  return (
                    <Link
                      key={item.href}
                      href={item.href}
                      className={cn(
                        'flex items-center space-x-3 px-3 py-2 rounded-md text-sm font-medium transition-colors',
                        pathname === item.href
                          ? 'bg-primary text-primary-foreground'
                          : 'hover:bg-accent hover:text-accent-foreground'
                      )}
                    >
                      {item.icon}
                      <span>{item.label}</span>
                    </Link>
                  );
                })}
              </>
            )}
          </nav>
        </ScrollArea>
      </aside>

      {open && (
        <div
          className="fixed inset-0 z-40 bg-background/80 backdrop-blur-sm lg:hidden"
          onClick={onClose}
        />
      )}
    </>
  );
}
